import { applySnapshot, flow, types } from "mobx-state-tree";
import api from "services/api";
import { startOfWeek, parseISO, formatISO, addDays } from "date-fns";
import WorkoutContentStore from "stores/WorkoutContentStore";
import { Context } from "models";
import { Category, Family } from "models/WorkoutCategory";
import { STRUCTURE_TYPE } from "constants/common";
import { CATEGORY_LIST_PAGE_SIZE, CONTENT_STORE_TYPE } from "constants/workout";
import { getWeekDay } from "utils/common";

const Workout = types
  .model("Workout", {
    context: types.maybe(Context),
    items: types.array(Category),
    details: types.maybe(Category),
    isLoading: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
    isDetailsLoading: types.optional(types.boolean, false),
    families: types.optional(types.array(Family), []),
    familyCategories: types.optional(types.array(Category), []),
    familyDetails: types.maybe(Family),
  })
  .actions(self => ({
    fillDetails() {
      const { details } = self;

      if (!details) {
        return;
      }

      if (details.structureType === STRUCTURE_TYPE.WEEK) {
        if (details.startDate) {
          const week: any = {};
          const start = startOfWeek(parseISO(details.startDate));

          [...Array(7).keys()].forEach(async count => {
            const date = addDays(start, count + 1);
            const key = getWeekDay(count);

            week[key] = WorkoutContentStore.create({
              type: CONTENT_STORE_TYPE.WEEK,
              dayOfWeek: count + 1,
              categoryId: details.id,
              date: formatISO(date),
            });
          });
          applySnapshot(self, {
            ...self,
            details: { ...details, week },
          });
        }
      } else {
        const standard = {
          all: WorkoutContentStore.create({
            type: CONTENT_STORE_TYPE.STANDARD_ALL,
            categoryId: details.id,
          }),
        };

        applySnapshot(self, {
          ...self,
          details: { ...details, standard },
        });
      }
    },
  }))
  .actions(self => ({
    fetch: flow(function* (
      page: number = 1,
      isFullAccessOnly: boolean = false,
    ) {
      try {
        if (page === 1) {
          self.isLoading = true;
        } else {
          self.isMoreLoading = true;
        }

        const { context, items } = yield api.get(
          "/mobile/workout/category/list",
          {
            size: CATEGORY_LIST_PAGE_SIZE,
            page,
            isFullAccessOnly,
          },
        );

        applySnapshot(self, {
          ...self,
          context,
          items: page === 1 ? items : self.items.concat(items),
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        if (page === 1) {
          self.isLoading = false;
        } else {
          self.isMoreLoading = false;
        }
      }
    }),

    fetchDetails: flow(function* (
      categoryId: number,
      isFullAccessOnly: boolean = false,
      familyId?: number | null 
    ) {
      self.isDetailsLoading = true;
      // TODO: Complete the refactor of fetchDetails calls to remove netcore endpoint
      try {
        if (familyId) {
          const response = yield api.get(
            `/workouts/api/v1/workouts/families/${familyId}/categories`,
            {
              isFullAccessOnly,
            },
            undefined,
            "MSURL"
          );
          const matched = response.data.find((item: any) => item.id === categoryId);
          
          if (matched) {
            applySnapshot(self, {
              ...self,
              details: matched,
            });
          } 
        } else {
          const details = yield api.get(
            `/mobile/workout/category/${categoryId}`,
            {
              isFullAccessOnly,
            }
          );
          applySnapshot(self, { ...self, details });
        }
      } catch (error) {
        console.log("fetchDetails error :>> ", error);
        throw error;
      } finally {
        self.isDetailsLoading = false;
      }
    }),

    fetchFamilies: flow(function* (isFullAccessOnly: boolean = false) {
      self.isLoading = true;
      try {
        const response = yield api.get(`/workouts/api/v1/workouts/families`, {
          isFullAccessOnly,
        },undefined,'MSURL');

        applySnapshot(self, {
          ...self,
          families: response.data,
        });
      } catch (error) {
        console.log("fetchFamilies error:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchFamilyCategories: flow(function* (
      familyId: number,
      isFullAccessOnly: boolean = false,
    ) {
      self.isLoading = true;
      try {
        const response = yield api.get(
          `/workouts/api/v1/workouts/families/${familyId}/categories`,
          {
            isFullAccessOnly,
          },
          undefined,
          "MSURL",
        );

        const data = response.data.map((item: any) => ({
          ...item,
          familyId: familyId,
        }));
        applySnapshot(self, {
          ...self,
          familyCategories: data,
        });
      } catch (error) {
        console.log("fetchFamilies error:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fillFamilyDetails(familyId: number) {
      const fam = WorkoutStore.families.find(f => f.id === familyId);
      if (!fam) {
        console.warn(`No encontré family con id=${familyId}`);
        return;
      }

      applySnapshot(self, {
        ...self,
        familyDetails: {
          id: fam.id,
          title: fam.title,
          structureType: fam.structureType,
          workoutsCategories: self.familyCategories,
        },
      })
    },

    reset() {
      applySnapshot(self, {});
    },

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

const WorkoutStore = Workout.create();

export default WorkoutStore;
